import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { DateTime } from "luxon";
import { useEffect, useLayoutEffect, useRef } from "react";
import { fetchMaintenanceNotice, type MaintenanceNotice, type MaintenanceNoticeResult, MaintenanceNoticeState } from "../../utils/maintenanceNotice";

/** How often the planned screen re-reads `maintenance.json`. */
export const PLANNED_RECHECK_INTERVAL_MS = 30_000;
/** While the app is showing, coming back to it re-reads `maintenance.json` at most this often. */
export const RESUME_RECHECK_MIN_GAP_MS = 60_000;

/** Runs `callback` when the app comes back into view: tab visible again, window focused, native app resumed. */
const subscribeToAppResume = (callback: () => void): (() => void) => {
	const onVisibilityChange = (): void => {
		if (document.visibilityState === "visible") callback();
	};
	document.addEventListener("visibilitychange", onVisibilityChange);
	window.addEventListener("focus", callback);
	// On web the App plugin re-emits visibilitychange as appStateChange, so it's only needed natively.
	const nativeListener = Capacitor.isNativePlatform()
		? CapacitorApp.addListener("appStateChange", ({ isActive }) => {
				if (isActive) callback();
			})
		: null;
	return () => {
		document.removeEventListener("visibilitychange", onVisibilityChange);
		window.removeEventListener("focus", callback);
		void nativeListener?.then((listener) => listener.remove());
	};
};

/** The latest callback, so effects can keep one timer across renders without going stale. */
const useLatest = <T>(value: T): { readonly current: T } => {
	const ref = useRef(value);
	useLayoutEffect(() => {
		ref.current = value;
	});
	return ref;
};

/** While `enabled`, re-reads `maintenance.json` every 30s and hands over what it found. */
export const usePlannedRecheck = (enabled: boolean, onResult: (result: MaintenanceNoticeResult) => void): void => {
	const latestOnResult = useLatest(onResult);
	useEffect(() => {
		if (!enabled) return;
		let disposed = false;
		const recheck = async (): Promise<void> => {
			const result = await fetchMaintenanceNotice();
			if (!disposed) latestOnResult.current(result);
		};
		const interval = setInterval(() => void recheck(), PLANNED_RECHECK_INTERVAL_MS);
		return () => {
			disposed = true;
			clearInterval(interval);
		};
	}, [enabled, latestOnResult]);
};

/**
 * While `enabled` (the app is showing), coming back to it re-reads `maintenance.json` — at most once a minute —
 * so a session left open picks up maintenance that started while it sat in the background.
 */
export const useNoticeRecheckOnResume = (enabled: boolean, onNotice: (notice: MaintenanceNotice) => void): void => {
	const latestOnNotice = useLatest(onNotice);
	useEffect(() => {
		if (!enabled) return;
		let disposed = false;
		let lastCheckedAt = DateTime.now();
		const recheck = async (): Promise<void> => {
			const now = DateTime.now();
			if (now.diff(lastCheckedAt).toMillis() < RESUME_RECHECK_MIN_GAP_MS) return;
			lastCheckedAt = now;
			const result = await fetchMaintenanceNotice();
			if (result.state === MaintenanceNoticeState.Active && !disposed) latestOnNotice.current(result.notice);
		};
		const unsubscribe = subscribeToAppResume(() => void recheck());
		return () => {
			disposed = true;
			unsubscribe();
		};
	}, [enabled, latestOnNotice]);
};
